import React, { useState } from 'react';
import { supabase } from '../utils/supabase';
import { getProfileStatus, getDashboardPath } from '../utils/profileCheck';
import Header from '../components/common/Header';
import PasswordInput from '../components/common/PasswordInput';
import '../styles/Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loginButtons = [
    { id: 1, label: 'Sign Up', path: '/signup' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      // If authentication fails, throw error immediately
      if (error) throw error;
      
      // Verify we actually got a user back
      if (!data || !data.user) {
        throw new Error('Invalid login credentials');
      }
      
      // Get user role from metadata (NO localStorage fallback for security)
      const userRole = data.user.user_metadata?.role;
      
      if (!userRole) {
        throw new Error('User role not found. Please contact support.');
      }

      // Check if user has created their profile
      const userId = data.user.id;
      const userEmail = data.user.email;
      const profileStatus = await getProfileStatus(userId, userRole, userEmail);

      if (profileStatus === 'error') {
        setError("We couldn't check your profile right now. Please try again in a moment.");
        return;
      }
      
      if (profileStatus === 'missing') {
        // User hasn't created profile yet, redirect to profile setup
        console.log('⚠️ Profile not found, redirecting to profile setup');
        window.location.href = '/profile-setup';
        return;
      }
      
      // Profile exists, redirect based on role
      console.log('✅ Profile exists, redirecting to role-based dashboard');
      const dashboardPath = getDashboardPath(userRole);
      if (!dashboardPath) {
        throw new Error('Invalid user role');
      }
      window.location.href = dashboardPath;
    } catch (err) {
      console.error('Login error details:', err);
      console.error('Error message:', err.message);
      console.error('Error status:', err.status);
      
      if (
        err.message &&
        (err.message.toLowerCase().includes('incorrect') ||
          err.message.toLowerCase().includes('invalid login credentials') ||
          err.message.toLowerCase().includes('invalid email') ||
          err.message.toLowerCase().includes('wrong password'))
      ) {
        setError('Incorrect email or password. Please try again.');
      } else if (err.message && err.message.toLowerCase().includes('email not confirmed')) {
        setError('Please confirm your email before logging in. Check your inbox for the confirmation link.');
      } else if (err.message && err.message.toLowerCase().includes('user not found')) {
        setError('No account found with this email. Please sign up first.');
      } else {
        // Show the actual error for debugging
        setError(`Login failed: ${err.message || 'Please try again.'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Header buttons={loginButtons} className="login-header" />
      <div className='login-main'>
        <img
          src={process.env.PUBLIC_URL + '/ITZ.LogoTypeFinalwithTag_AvenirNext.Web.png'}
          alt="ITZ Main Logo"
          className="login-logo"
      />
      <div className='login-form-container'>
          <form onSubmit={handleSubmit}  className="login-form">
            {error && (
              <div className="login-error">
                {error}
              </div>
            )}
            <div className="login-form-group">
              <label className="login-label">Email:</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="login-input"
                placeholder="your.email@example.com"
              />
            </div>
            <div className="login-form-group">
              <label className="login-label">Password:</label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="login-input"
                wrapClassName="login-password-wrap"
              />
            </div>
            <div className="login-submit-row">
            <button type="submit" disabled={loading} className="login-submit app-btn">
              {loading ? 'Loading...' : 'Submit'}
            </button>
            <div className="login-forgot-block">
              <div className="login-forgot-inline">
                <a href="/reset-password" className="login-forgot-link">Forgot Password </a>
              </div>
            </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;